"use strict";
var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __param = (this && this.__param) || function (paramIndex, decorator) {
    return function (target, key) { decorator(target, key, paramIndex); }
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.AuthModule = void 0;
// external
const inversify_1 = require("inversify");
// local
const EventEmitter_1 = require("../../util/EventEmitter");
let AuthModule = class AuthModule extends EventEmitter_1.EventEmitter {
    /**
     * Setup injected dependencies.
     */
    constructor(_httpConnection, _webSocketConnection, _syncModule, GenericSessionCls, GenericLocationCls) {
        super();
        this._httpConnection = _httpConnection;
        this._webSocketConnection = _webSocketConnection;
        this._syncModule = _syncModule;
        this.GenericSessionCls = GenericSessionCls;
        this.GenericLocationCls = GenericLocationCls;
        this._session = null; // current session
        this._sessionTemporaryAccessGranted = false;
    }
    setActiveLocation(location) {
        this._activeLocation = location;
        localStorage.setItem("activeLocationSlug", location.slug);
    }
    get activeLocation() {
        return this._activeLocation;
    }
    get availableLocations() {
        return this._availableLocations;
    }
    setAvailableLocations(locations) {
        this._availableLocations = locations;
    }
    /**
     * Get current session.
     */
    get session() {
        return this._session;
    }
    /**
     * Set new session.
     */
    setSession(newSession) {
        // ensure new instance is present and a session model
        if (newSession !== null &&
            !(newSession instanceof this.GenericSessionCls)) {
            throw new Error("Cannot setSession(...) with non session object");
        }
        // set the session with the new instance
        this._session = newSession;
        // if new session is not present reset the http and web socket
        // connection sessions and emit a "sessionChange" event
        if (newSession === null) {
            this._httpConnection.bearerToken = null;
            this._webSocketConnection.sessionToken = null;
            this._syncModule.sessionToken = null;
            this.emit("sessionChange");
            return;
        }
        // set available locations and active current location
        this.setAvailableLocations(newSession.availableLocations);
        // if (!this.availableLocations.length) {
        //   throw new Error("There are no available locations");
        // }
        if (this.availableLocations.length) {
            let activeLocationSlug = localStorage.getItem("activeLocationSlug");
            if (!activeLocationSlug) {
                const activeLocationObj = localStorage.getItem("activeLocation");
                if (activeLocationObj) {
                    const activeLocation = JSON.parse(activeLocationObj);
                    if (activeLocation) {
                        activeLocationSlug = activeLocation.slug;
                    }
                }
            }
            if (activeLocationSlug) {
                let location = this.availableLocations.find((location) => location.slug === activeLocationSlug);
                if (location === undefined) {
                    location = this.availableLocations[0];
                }
                this.setActiveLocation(location);
            }
            else {
                this.setActiveLocation(this.availableLocations[0]);
            }
        }
        // otherwise we set the http and web socket connection session to the
        // new instance and emit a "sessionChange" event
        this._httpConnection.bearerToken = newSession.sessionId;
        this._webSocketConnection.sessionToken = newSession.sessionId;
        this._syncModule.sessionToken = newSession.sessionId;
        this.emit("sessionChange");
    }
    /**
     * Set new session from given session id.
     */
    async setSessionFromSessionId(sessionId) {
        // get current session for current user
        const res = await this._httpConnection.makeRequest({
            url: "auth/me",
            method: "get",
            overrideBearerToken: sessionId
        });
        // get session details
        try {
            const session = res.session
                ? this.GenericSessionCls.unserialize(res.session)
                : null;
            // set new session
            this.setSession(session);
            // return session
            return session;
        }
        catch (e) {
            this.setSession(null);
            return null;
        }
    }
    /**
     * Get session for current user.
     */
    async me(skipSetSession) {
        // get session for current user
        const res = await this._httpConnection.makeRequest({
            url: "auth/me",
            method: "get"
        });
        // get session details
        const session = res.session
            ? this.GenericSessionCls.unserialize(res.session)
            : null;
        // set new session
        if (!skipSetSession) {
            this.setSession(session);
        }
        // return session
        return session;
    }
    /**
     * Temporary access
     */
    get sessionTemporaryAccessGranted() {
        return this._sessionTemporaryAccessGranted;
    }
    grantSessionTemporaryAccess() {
        // make sure we have a session
        if (this._session === null)
            return;
        this._sessionTemporaryAccessGranted = true;
        this.emit("sessionTemporaryAccessChange");
    }
    revokeSessionTemporaryAccess() {
        this._sessionTemporaryAccessGranted = false;
        this.emit("sessionTemporaryAccessChange");
    }
    /**
     * Log out current user.
     */
    async logout() {
        // emit "beforeLogout" event for any listeners
        this.emit("beforeLogout");
        // perform logout request
        await this._httpConnection.makeRequest({
            url: "auth/logout",
            method: "post"
        });
        // reset session
        this.setSession(null);
        // reset sessionTemporaryAccess if true
        if (this._sessionTemporaryAccessGranted === true) {
            this.revokeSessionTemporaryAccess();
        }
    }
    /**
     * Login as an app.
     */
    async loginAsApp(accessCode) {
        const res = await this._httpConnection.makeRequest({
            url: "auth/login-as-app",
            method: "post",
            data: { accessCode }
        });
        const session = res.session
            ? this.GenericSessionCls.unserialize(res.session)
            : null;
        this.setSession(session);
        return session;
    }
};
AuthModule = __decorate([
    (0, inversify_1.injectable)(),
    __param(0, (0, inversify_1.inject)("HttpConnection")),
    __param(1, (0, inversify_1.inject)("WebSocketConnection")),
    __param(2, (0, inversify_1.inject)("SyncModule")),
    __param(3, (0, inversify_1.inject)("interfaces.Newable<GenericSession>")),
    __param(4, (0, inversify_1.inject)("interfaces.Newable<Location>"))
], AuthModule);
exports.AuthModule = AuthModule;
//# sourceMappingURL=AuthModule.js.map